import React, { useEffect, useMemo, useState } from 'react';
import { Col, Flex as AntdFlex, Flex, List, Row, Table, Tag } from 'antd';
import { useLocation, useParams } from 'react-router-dom';
import {
  CustomsOfficeData,
  GetAirportsData,
  getCustomsOfficeByPortCode,
} from '../../../apis/airports';
import { useSelector } from 'react-redux';
import { useToken } from '../../../common/helper';
import { CustomParagraph, CustomTitle } from '../../../components';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../../components/GoogleMapsProvider';
import { getMapStyles } from '../../../common/GoogleMapStyle';
import type { RootState } from '../../../redux-toolkit/store';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { LatLng } from '@/apis/types';

const mapContainerStyle = {
  width: '100%',
  height: '208px',
  borderRadius: '12px',
  marginBottom: '24px',
  overflow: 'hidden',
};

const formatPhoneNumber = (number: string | undefined): string => {
  if (!number) return '';
  const cleaned = number.replace(/[^\d]/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return number;
};

const isValidPhone = (value: string | undefined): boolean => {
  if (!value) return false;
  const cleaned = value.replace(/[^\d]/g, '');
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === '1');
};

const cleanAddress = (address: string | undefined): string => {
  if (!address) {
    return '--';
  }

  // Step 1: Clean the initial address string
  let cleaned = address.replace(/\s*\n+\s*/g, ', ');
  cleaned = cleaned.replace(/\s+/g, ' ');
  cleaned = cleaned.replace(/(, ?)+/g, ', ');
  cleaned = cleaned.replace(/^, |, $/g, '');

  // Step 2: Apply the new formatting rules based on comma position and total parts
  const parts = cleaned.split(', ');
  const finalParts = [];
  const numParts = parts.length;

  if (numParts > 0) {
    finalParts.push(parts[0]);
  }

  // Iterate through the rest of the parts (from the second part onwards)
  for (let i = 1; i < numParts; i++) {
    let separator = ', '; // Default separator for subsequent parts

    // Check if we are in the "5 or more commas" scenario
    if (numParts >= 6) {
      if (i === 1) {
        separator = ' ';
      } else if (i === 2) {
        separator = '\n';
      } else if (i === 3) {
        separator = ', ';
      } else if (i === 4) {
        separator = ' ';
      } else if (i === 5) {
        separator = '\n';
      }
    } else {
      if (i === 1) {
        separator = '\n';
      } else if (i === 2) {
        separator = ', ';
      } else if (i === 3) {
        separator = ' ';
      } else if (i === 4) {
        separator = '\n';
      }
    }
    finalParts.push(separator, parts[i]);
  }

  // Join all the processed parts back into a single string
  return finalParts.join('');
};

// Helper function to format 24hr time to AM/PM
const formatTimeForDisplay = (time: string | undefined): string => {
  if (!time) return 'Closed';
  try {
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return 'Closed';
    }

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting time:', time, error);
    return 'Closed';
  }
};

/**
 * CustomsOffice component displays customs office information for an airport
 */
const CustomsOffice: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const location = useLocation();
  const { token } = useToken();
  const [customOffice, setCustomOffice] = useState<CustomsOfficeData>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [airportLatLng, setAirportLatLng] = useState<LatLng>(null);
  const [portCodeState, setPortCodeState] = useState<string>(null);
  const [isOfficeHoursCollapsed, setIsOfficeHoursCollapsed] =
    useState<boolean>(false);

  const { isLoaded } = useGoogleMaps();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const mapStyles = useMemo(
    () => getMapStyles(darkModeEnabled),
    [darkModeEnabled]
  );

  // Use useMemo for defaultCenter to ensure stable reference
  const defaultCenter = useMemo(() => ({ lat: 40.6413, lng: -73.7781 }), []); // JFK Airport

  // Effect 1: Fetch Airport Data and extract portCode
  useEffect(() => {
    const airportCodeUpperCase = code.toUpperCase();
    const airlineType = 2; // Hardcode '2' for Delta for now
    const queryForAirportData = `${airportCodeUpperCase}/airlines/?type=${airlineType}`;

    let isMounted = true;

    GetAirportsData(queryForAirportData, token)
      .then((res) => {
        if (!isMounted) return;
        console.log('GetAirportsData response:', res);

        const airportData = res.data?.data?.data?.[0];

        let lat = null;
        let lng = null;
        let extractedPortCode = null;

        if (airportData) {
          lat = parseFloat(airportData.latitude || '');
          lng = parseFloat(airportData.longitude || '');
          extractedPortCode = airportData.portCode || null;
        }

        if (!isNaN(lat) && !isNaN(lng)) {
          setAirportLatLng({ lat, lng });
        } else {
          console.warn(
            `Invalid airport coordinates for ${airportCodeUpperCase}. Falling back to default. Response:`,
            res
          );
          setAirportLatLng(defaultCenter);
        }

        if (extractedPortCode) {
          setPortCodeState(extractedPortCode);
        } else {
          console.warn(
            `Port Code not found in response for ${airportCodeUpperCase}. Response:`,
            res
          );
          setPortCodeState('NOT_FOUND'); // Set a distinct value to indicate not found, but prevent re-fetch loop
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Error fetching airport data:', err);
        setAirportLatLng(defaultCenter);
        setPortCodeState('ERROR'); // Set a distinct error state for portCode
      });

    return () => {
      isMounted = false;
    };
  }, [code, token, defaultCenter]);

  // Effect 2: Fetch Customs Office by portCode
  useEffect(() => {
    if (
      portCodeState &&
      portCodeState !== 'NOT_FOUND' &&
      portCodeState !== 'ERROR'
    ) {
      setLoading(true);
      let isMounted = true;

      getCustomsOfficeByPortCode(portCodeState, token)
        .then((res) => {
          if (!isMounted) return;
          console.log('Customs Office by Port Code response:', res);

          const officeDataPayload = res.data;

          if (officeDataPayload) {
            // If the payload is an array, take the first item
            if (
              Array.isArray(officeDataPayload) &&
              officeDataPayload.length > 0
            ) {
              setCustomOffice(officeDataPayload[0]);
            }
            // If the payload is a direct object
            else if (!Array.isArray(officeDataPayload)) {
              setCustomOffice(officeDataPayload);
            } else {
              console.warn(
                `Empty array or unexpected structure for customs customOffice data for portCode: ${portCodeState}. Response:`,
                res
              );
              setCustomOffice(null);
            }
          } else {
            console.warn(
              `No customs customOffice data payload found in response for portCode: ${portCodeState}. Response:`,
              res
            );
            setCustomOffice(null);
          }
          setLoading(false);
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error(
            `Error fetching customs customOffice by portCode ${portCodeState}:`,
            err
          );
          setCustomOffice(null);
          setLoading(false);
        });
      return () => {
        isMounted = false;
      };
    } else if (portCodeState === 'NOT_FOUND' || portCodeState === 'ERROR') {
      // If portCode was determined to be not found or an error occurred in the first effect,
      // then we can stop loading for the entire component.
      setLoading(false);
      setCustomOffice(null);
    }
  }, [portCodeState, token]);

  const breadcrumbItems = [
    {
      title: 'Airports',
      href: '/airport-list',
    },
    {
      title: location.state?.airportName || code,
      href: `/airport-list/airport/${code}`,
      state: {
        airport: {
          code: code,
          name: location.state?.airportName || code,
          IATA: code,
        },
      },
    },
    {
      title: 'Customs',
    },
  ];

  // Logic to determine if the office is open or closed
  const isOfficeOpen = useMemo(() => {
    if (
      !customOffice ||
      !customOffice.office_hours ||
      customOffice.office_hours.length === 0
    ) {
      return null; // No hours provided, cannot determine status
    }

    const now = new Date();
    const currentDay = now.toLocaleString('en-US', { weekday: 'long' }); // Get full day name: Monday, Tuesday, etc.
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes since midnight

    for (const hourBlock of customOffice.office_hours) {
      const officeDay = hourBlock.day; // Assuming this is already a full day name (e.g., "Monday")

      if (officeDay && officeDay.toLowerCase() === currentDay.toLowerCase()) {
        if (!hourBlock.open_time || !hourBlock.close_time) continue;

        const [openHour, openMinute] = hourBlock.open_time
          .split(':')
          .map(Number);
        let [closeHour, closeMinute] = hourBlock.close_time
          .split(':')
          .map(Number);

        // Treat 24:00 (12:00 AM) as 23:59 (11:59 PM) for closing times
        if (closeHour === 0 && closeMinute === 0) {
          // If closing time is 00:00 (midnight)
          closeHour = 23;
          closeMinute = 59;
        }

        const openTimeInMinutes = openHour * 60 + openMinute;
        const closeTimeInMinutes = closeHour * 60 + closeMinute;

        if (
          openTimeInMinutes <= currentTime &&
          currentTime <= closeTimeInMinutes
        ) {
          return true; // Open now
        }
      }
    }
    return false; // Closed
  }, [customOffice]);

  if (loading)
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          fontSize: '18px',
          color: 'var(--text-secondary)',
        }}
      >
        Loading customs office details...
      </div>
    );

  if (!customOffice) {
    return (
      <section className='airline-list'>
        <Flex vertical gap={16}>
          <Breadcrumbs items={breadcrumbItems} />
          <CustomTitle
            text={`${location.state?.airportName || location.state?.iataCode || code} – Customs`}
            fontSize='24px'
            fontWeight='700'
          />
          <CustomParagraph
            text={
              portCodeState === 'NOT_FOUND'
                ? `Port Code not found in airport data for ${code}. Cannot fetch customs office.`
                : portCodeState === 'ERROR'
                  ? `Error retrieving port code for ${code}. Cannot fetch customs office.`
                  : `No customs office data found for port code ${portCodeState || 'unknown'}.`
            }
            fontSize='16px'
            color='var(--text-tertiary)'
            style={{ padding: '20px', textAlign: 'center' }}
          />
        </Flex>
      </section>
    );
  }

  const validTypes = ['phone', 'fax', 'email'];
  const isValidEmail = (value: string | undefined): boolean =>
    !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // filter contacts to only include valid types and non-empty values
  const allValidContacts = (customOffice.contacts || []).filter((contact) => {
    const type = contact.contact_type?.toLowerCase();
    if (!validTypes.includes(type)) return false;
    if (!contact.contact_value || !contact.contact_value.trim()) return false;
    if (type === 'phone' || type === 'fax') {
      return isValidPhone(contact.contact_value);
    }
    if (type === 'email') {
      return isValidEmail(contact.contact_value);
    }
    return false;
  });

  // check if any "Primary" contacts exist among all valid contacts
  const primaryContactsExist = allValidContacts.some(
    (contact) => contact.contact_title?.toLowerCase() === 'primary'
  );

  // determine which contacts to display based on the "Primary" existence
  const contactsToDisplay = primaryContactsExist
    ? allValidContacts.filter(
        (contact) => contact.contact_title?.toLowerCase() === 'primary'
      )
    : allValidContacts;

  interface GroupedContacts {
    [key: string]: typeof allValidContacts;
  }

  interface ContactTableData {
    key: string;
    title: string;
    type?: string;
    value?: string;
  }

  const grouped = contactsToDisplay.reduce<GroupedContacts>((acc, contact) => {
    const title = contact.contact_title || 'Other';
    if (!acc[title]) acc[title] = [];
    acc[title].push(contact);
    return acc;
  }, {});

  const contactTableData: ContactTableData[] = Object.entries(grouped).flatMap(
    ([title, contacts]) =>
      contacts.map((contact, i: number) => ({
        key: `${title}-${i}`,
        title,
        type: contact.contact_type,
        value: contact.contact_value,
      }))
  );

  const contactColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 120,
      render: (text: string, _row: ContactTableData, idx: number) =>
        idx === 0 || contactTableData[idx - 1]?.title !== text ? (
          <span
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--text-primary)',
            }}
          >
            {text}
          </span>
        ) : null,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (text: string) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: 12,
            color: 'var(--text-primary)',
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: string, row: ContactTableData) => (
        <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
          {row.type?.toLowerCase() === 'email' ? (
            <a
              href={`mailto:${value}`}
              style={{
                color: 'var(--text-primary)',
                textDecoration: 'underline',
              }}
            >
              {value}
            </a>
          ) : row.type?.toLowerCase() === 'phone' ||
            row.type?.toLowerCase() === 'fax' ? (
            formatPhoneNumber(value)
          ) : (
            value
          )}
        </span>
      ),
    },
  ];

  const disclaimerText = (
    <p className='zl-page-content'>
      The data on this page is sourced from the US Customs and Border Protection
      website and may be outdated or inaccurate. Always verify details with the{' '}
      {customOffice?.source_url ? (
        <a
          href={customOffice.source_url}
          target='_blank'
          rel='noopener noreferrer'
          style={{ color: 'var(--text-primary)', textDecoration: 'underline' }}
        >
          official website
        </a>
      ) : (
        'official website'
      )}
      .
    </p>
  );

  return (
    <section className='airline-list'>
      <Flex vertical gap={16}>
        <Breadcrumbs items={breadcrumbItems} />
        <CustomTitle
          text={`${location.state?.airportName || location.state?.iataCode || code} – Customs`}
          fontSize='24px'
          fontWeight='700'
        />

        {customOffice.port_code && (
          <CustomParagraph
            text={`Port Code: ${customOffice.port_code}`}
            fontSize='16px'
            fontWeight={500}
            color='var(--text-primary)'
            style={{ marginBottom: 8 }}
          />
        )}

        <div
          key={customOffice.port_code}
          className='customs-card'
          style={{
            background: 'var(--surface-container)',
            borderRadius: 12,
            padding: 24,
            marginBottom: 24,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
          }}
        >
          <Row gutter={32}>
            {/* Left Column: Office Hours */}
            <Col xs={24} md={14}>
              <AntdFlex
                align='center'
                gap={8}
                onClick={() =>
                  setIsOfficeHoursCollapsed(!isOfficeHoursCollapsed)
                }
                style={{
                  marginBottom: 16,
                  cursor: 'pointer',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <AntdFlex align='center' gap={8}>
                  <CustomTitle
                    text='Office Hours'
                    fontSize='18px'
                    fontWeight='600'
                    marginBottom='0'
                  />
                  {/* Open/Closed Indicator */}
                  {isOfficeOpen !== null && (
                    <Tag
                      className={
                        isOfficeOpen
                          ? 'zl-custom-success-tag'
                          : 'zl-custom-closed-tag'
                      }
                    >
                      {isOfficeOpen ? 'Open now' : 'Closed'}
                    </Tag>
                  )}
                </AntdFlex>
                {/* Increased font size for icons */}
                {isOfficeHoursCollapsed ? (
                  <DownOutlined style={{ fontSize: '18px' }} />
                ) : (
                  <UpOutlined style={{ fontSize: '18px' }} />
                )}
              </AntdFlex>
              {!isOfficeHoursCollapsed &&
                (customOffice.office_hours &&
                customOffice.office_hours.length > 0 ? (
                  <AntdFlex justify='space-between' style={{ width: '100%' }}>
                    <List
                      className='zl-opening-hours-list zl-custom-collapse-data'
                      style={{ width: '100%' }}
                      dataSource={customOffice.office_hours.map((hour, i) => ({
                        key: i,
                        day: hour.day || 'Unknown Day',
                        open_time: hour.open_time,
                        close_time: hour.close_time,
                      }))}
                      renderItem={(item) => (
                        <List.Item style={{ padding: '8px 16px' }}>
                          <AntdFlex align='center' style={{ width: '100%' }}>
                            <span
                              className='list-item'
                              style={{ fontWeight: 500, fontSize: '14px' }}
                            >
                              {item.day}
                            </span>
                            <div
                              className='zl-badge-item'
                              style={{ marginLeft: 'auto' }}
                            >
                              <time className='list-item'>
                                {/* Display "Closed" if open_time or close_time are not available for the day */}
                                {item.open_time && item.close_time
                                  ? `${formatTimeForDisplay(item.open_time)} – ${formatTimeForDisplay(item.close_time)}`
                                  : 'Closed'}
                              </time>
                            </div>
                          </AntdFlex>
                        </List.Item>
                      )}
                    />
                  </AntdFlex>
                ) : (
                  <CustomParagraph
                    text='No office hours available.'
                    fontSize='14px'
                    color='var(--text-tertiary)'
                  />
                ))}
            </Col>

            {/* Right Column: Map and Contacts */}
            <Col xs={24} md={10}>
              <Flex vertical gap={24} className='zl-company-info'>
                {isLoaded && (airportLatLng || defaultCenter) && (
                  <div style={{ ...mapContainerStyle, marginBottom: 16 }}>
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '208px' }}
                      center={airportLatLng || defaultCenter}
                      zoom={12}
                      options={{ styles: mapStyles }}
                    >
                      {airportLatLng &&
                        typeof airportLatLng.lat === 'number' &&
                        !isNaN(airportLatLng.lat) &&
                        typeof airportLatLng.lng === 'number' &&
                        !isNaN(airportLatLng.lng) && (
                          <Marker
                            position={airportLatLng}
                            title={`${location.state?.airportName || code} Airport`}
                          />
                        )}
                    </GoogleMap>
                  </div>
                )}
                <Flex vertical gap={24} className='zl-company-detail'>
                  {/* Office Location Section */}
                  <Flex vertical gap={4}>
                    <CustomTitle
                      text='Office Location'
                      fontSize='16px'
                      fontWeight='600'
                      lineHeight='20px'
                      color='var(--text-primary)'
                      marginBottom='0'
                      marginTop='0'
                    />
                    {cleanAddress(customOffice.location_address) !== '--' && (
                      <CustomParagraph
                        text={`${cleanAddress(customOffice.location_address)}`}
                        fontSize='14px'
                        fontWeight={400}
                        color='var(--text-primary)'
                        style={{ marginTop: '4px', marginBottom: 16 }}
                      />
                    )}
                  </Flex>

                  <Flex vertical gap={4}>
                    <CustomTitle
                      text='Contacts'
                      fontSize='16px'
                      fontWeight='600'
                      lineHeight='20px'
                      color='var(--text-primary)'
                      marginBottom='0'
                      marginTop='0'
                    />
                    {contactTableData.length > 0 ? (
                      <Table
                        columns={contactColumns}
                        dataSource={contactTableData}
                        pagination={false}
                        bordered={false}
                        showHeader={false}
                        rowClassName={() => 'airline-contacts-table'}
                        style={{
                          background: 'var(--surface-container)',
                          marginTop: '4px',
                        }}
                      />
                    ) : (
                      <CustomParagraph
                        text='No contacts available.'
                        fontSize='12px'
                        color='var(--text-tertiary)'
                        style={{ marginTop: '4px' }}
                      />
                    )}
                  </Flex>
                </Flex>
              </Flex>
              {disclaimerText}
            </Col>
          </Row>
        </div>
      </Flex>
    </section>
  );
};

export default CustomsOffice;
